const pendingByAssetId = new Map();

const ensureEntry = (assetId) => {
  const key = String(assetId);
  if (!pendingByAssetId.has(key)) {
    pendingByAssetId.set(key, { products: [], productVariants: [] });
  }
  return pendingByAssetId.get(key);
};

const pruneEmptyEntry = (assetId) => {
  const key = String(assetId);
  const entry = pendingByAssetId.get(key);
  if (!entry) return;
  if (!entry.products.length && !entry.productVariants.length) {
    pendingByAssetId.delete(key);
  }
};

/**
 * Remember optimistic Published-mode product links so fail events can roll them back.
 */
export const registerPendingPublishAttachLinks = ({
  assetIds = [],
  product,
  productVariants = [],
} = {}) => {
  if (!assetIds.length) return;

  assetIds.forEach((assetId) => {
    const entry = ensureEntry(assetId);
    if (product?._id) {
      entry.products.push(product);
    }
    if (productVariants.length) {
      entry.productVariants.push(...productVariants);
    }
  });
};

/**
 * Take and clear pending gallery product links (productAttaching.failed).
 */
export const takePendingPublishAttachProducts = (assetIds = []) => {
  const products = [];
  const resolvedAssetIds = [];

  assetIds.forEach((assetId) => {
    const key = String(assetId);
    const entry = pendingByAssetId.get(key);
    if (!entry?.products.length) return;

    resolvedAssetIds.push(key);
    products.push(...entry.products);
    entry.products = [];
    pruneEmptyEntry(key);
  });

  return { assetIds: resolvedAssetIds, products, productVariants: [] };
};

/**
 * Take and clear pending variant links (variantAttaching.failed).
 */
export const takePendingPublishAttachVariants = (assetIds = []) => {
  const productVariants = [];
  const resolvedAssetIds = [];

  assetIds.forEach((assetId) => {
    const key = String(assetId);
    const entry = pendingByAssetId.get(key);
    if (!entry?.productVariants.length) return;

    resolvedAssetIds.push(key);
    productVariants.push(...entry.productVariants);
    entry.productVariants = [];
    pruneEmptyEntry(key);
  });

  return { assetIds: resolvedAssetIds, products: [], productVariants };
};

/**
 * Drop pending product (gallery) links after productAttaching.complete.
 * Leaves pending variant links intact when both were attached together.
 */
export const clearPendingPublishAttachProducts = (assetIds = []) => {
  assetIds.forEach((assetId) => {
    const key = String(assetId);
    const entry = pendingByAssetId.get(key);
    if (!entry) return;
    entry.products = [];
    pruneEmptyEntry(key);
  });
};

/**
 * Drop pending variant links after variantAttaching.complete.
 */
export const clearPendingPublishAttachVariants = (assetIds = []) => {
  assetIds.forEach((assetId) => {
    const key = String(assetId);
    const entry = pendingByAssetId.get(key);
    if (!entry) return;
    entry.productVariants = [];
    pruneEmptyEntry(key);
  });
};

/** @private test helper */
export const __resetPendingPublishAttachLinks = () => {
  pendingByAssetId.clear();
};
